import { Component, inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { FormularioService } from '../../services/database/formulario.service';
import { AccessRole, CreateFormRequest, CustomTitle, Form, FormCreatorService, FormGroupErrorMessageComponent, FormNoData, LoadingPanelService, LocalService, noWhitespaceValidator, UpdateFormRequest, Utils } from 'lib-base';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { DropdownModule } from "primeng/dropdown";
import { ICategory } from "../../shared/interfaces/database/formulario";
import { ToolbarModule } from 'primeng/toolbar';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ListboxClickEvent, ListboxModule } from 'primeng/listbox';
import { CustomTitleDirective } from '../../shared/clases/custom-title.directive';

@Component({
  selector: 'app-form-save-manager',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule,
    DialogModule, ButtonModule, InputTextModule, InputTextareaModule,
    FormGroupErrorMessageComponent, ToastModule, CustomTitle, DropdownModule,
    ToolbarModule, OverlayPanelModule, ListboxModule
  ],
  providers: [MessageService],
  templateUrl: './form-save-manager.component.html',
  styleUrl: './form-save-manager.component.scss'
})
export class FormSaveManagerComponent implements OnInit {

  @Input() fc !: FormCreatorService;
  @Input() form !: FormNoData;
  displayModal: boolean = false;
  formGroup !: FormGroup;
  role = AccessRole;
  trans: any = {};
  categories: ICategory[] = [];
  referencias !: FormNoData[];
 private titleService:CustomTitleDirective = inject(CustomTitleDirective);

  constructor(private messageService: MessageService, private formDB: FormularioService,
    public ls: LocalService, private _lps: LoadingPanelService, private _t: TranslateService
  ) {
    this.formGroup = new FormGroup({
      name: new FormControl(null, [Validators.required, noWhitespaceValidator, Validators.maxLength(250)]),
      description: new FormControl(null, [Validators.maxLength(250)]),
      category: new FormControl(null, [Validators.required, Validators.maxLength(255)])
    });
  }

  ngOnInit(): void {
    this.translate();
    this.getCategories();
    this.setTabName()
    this.findSubformularioReferenceias();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      exito: this._t.get('common.exito'),
      advertencia: this._t.get('common.advertencia'),
      noCambiosFormulario: this._t.get('msg.noCambiosFormulario'),
      completeCamposRequeridos: this._t.get('msg.completeCamposRequeridos'),
    }));
  }

  openModal() {
    this.formGroup.reset();
    if (this.form) {
      this.formGroup.get('name')?.setValue(this.form.nombre);
      this.formGroup.get('description')?.setValue(this.form.descripcion);
      this.formGroup.get('category')?.setValue(this.form.categoria);
    }
    this.displayModal = true;
  }

  private saveNewForm() {
    const createFormRequest = {
      nombre: this.formGroup.get('name')?.value,
      descripcion: this.formGroup.get('description')?.value,
      categoria: this.formGroup.get('category')?.value ?? null,
      contenido: this.fc.getFormAsString()
    } as CreateFormRequest;

    this._lps.show(true);
    this.formDB.createForm(createFormRequest).subscribe({
      next: (formId: number) => {
        this._lps.hide();
        let newForm = {
          id: formId,
          nombre: createFormRequest.nombre,
          descripcion: createFormRequest.descripcion,
          categoria: createFormRequest.categoria
        } as FormNoData
        this.fc.LoadedForm = newForm;
        this.setTabName()
        this.findSubformularioReferenceias();
        this.displayModal = false;
        this.messageService.add({ severity: 'success', summary: 'Save Success', detail: 'Formulario guardado' });
      }
    });
  }

  private updateForm() {
    const updateFormRequest = {
      id: this.form.id,
      nombre: this.formGroup.get('name')?.value,
      descripcion: this.formGroup.get('description')?.value,
      categoria: this.formGroup.get('category')?.value,
      contenido: this.fc.getFormAsString()
    } as UpdateFormRequest;

    this._lps.show(true);
    this.formDB.updateForm(updateFormRequest).subscribe({
      next: (response: string) => {
        this._lps.hide();
        let newForm = {
          id: this.form.id,
          nombre: updateFormRequest.nombre,
          descripcion: updateFormRequest.descripcion,
          categoria: updateFormRequest.categoria
        } as FormNoData
        this.fc.LoadedForm = newForm;
        this.setTabName()
        this.findSubformularioReferenceias();
        this.displayModal = false;
        this.messageService.add({ severity: 'success', summary: 'Update Success', detail: 'Form updated' });
      }
    });
  }

  onSubmit() {
    if (this.formGroup.invalid) {
      Utils.markAllFormControlsAsTouched(this.formGroup);
      this.messageService.add({ severity: 'warn', summary: 'Advertencia', detail: this.trans.completeCampos });
      return;
    }

    if (this.form)
      this.updateForm();
    else
      this.saveNewForm();
  }

  private getCategories() {
    this.formDB.getCategoriesAvailable().subscribe({
      next: (response: ICategory[]) => {
        this.categories = response;
      }
    })
  }

  private setTabName(){
    if (!this.fc.LoadedForm) return;
    this.titleService.changeTitle(this.fc.LoadedForm.nombre);
  }

  //#region Referencias
  private findSubformularioReferenceias() {
    if (!this.fc.LoadedForm) return;
    this.formDB.findSubformularioReferenceias(this.fc.LoadedForm.id).subscribe({
      next: (response: FormNoData[]) => {
        this.referencias = response;
      }
    });
  }

  openForm($event: ListboxClickEvent) {
    const url = `#/configuracion/formulario/edit/${$event.option.id}`
    window.open(url, '_blank');
  }
  //#endregion Referencias
}
